import { ReactNode } from 'react';
import styled from 'styled-components';
import { space, layout, overflow, textAlign, TextAlignProps, OverflowProps, LayoutProps } from 'styled-system';
import { SpaceProps, WidthProps } from 'styled-system';
import { black } from '../constants/Colors';
import { color, ColorProps } from '../utils/styled-components/styledSystem';
import { TextSize, textSizeVariant } from './variants/textSizeVariant';
import { textOverflow, TextOverflowProps } from '../utils/styled-components/textOverflow';
import { whiteSpace, WhiteSpaceProps } from '../utils/styled-components/whiteSpace';
import { wordBreak, WordBreakProps } from '../utils/styled-components/wordBreak';
import { fontFamily, FontFamilyProps } from '../utils/styled-components/fontFamily';
import { fontWeight, FontWeightProps } from '../utils/styled-components/fontWeight';

export type TextProps = ColorProps &
    SpaceProps &
    WidthProps &
    FontWeightProps &
    OverflowProps &
    LayoutProps &
    TextAlignProps &
    TextOverflowProps &
    WhiteSpaceProps &
    WordBreakProps &
    FontFamilyProps & {
        /**
         * ```
         * xxsmall: '10px'
         * xsmall: '12px'
         * small: '14px'
         * medium: '16px'
         * large:  '18px'
         * xlarge: '20px'
         * xxlarge: '22px'
         * xxxlarge: '24px'
         * ```
         * @defaultValue 'medium'
         */
        textSize?: TextSize;
        maxLines?: number;
        children: ReactNode;
    };

/**
 * Text component. Rendered as p tag.
 */
export const Text = styled.p<TextProps>`
    ${textSizeVariant}
    ${fontWeight}
    ${textAlign}
    ${space}
    ${layout}
    ${color}
    ${overflow}
    ${textOverflow}
    ${whiteSpace}
    ${wordBreak}
    ${fontFamily}
    ${({ maxLines }) =>
        maxLines &&
        `
        @supports (-webkit-line-clamp: ${maxLines}) {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: initial;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: ${maxLines};
            display: -webkit-box;
        }
    `}
`;

Text.displayName = 'Text';

Text.defaultProps = {
    textColor: black,
    fontWeight: '400',
    margin: '1em 0 1em 0',
    padding: '0',
    textSize: 'medium',
    whiteSpace: 'normal',
    textOverflow: 'clip',
};
